import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Plus, BriefcaseMedical, Edit, Trash2, Lock } from 'lucide-react';
import { usePet } from '@/hooks/usePet';
import { useAuth } from '@/contexts/AuthContext';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useVaccinations } from '@/hooks/useVaccinations';
import { useAppointments } from '@/hooks/useAppointments';
import { useMedicalRecords } from '@/hooks/useMedicalRecords';
import { format } from 'date-fns';
import MedicalReminderSettings from '@/components/MedicalReminderSettings';
import { DeleteConfirmDialog } from '@/components/DeleteConfirmDialog';
import { useToast } from '@/hooks/use-toast';
import { useSubscription } from '@/hooks/useSubscription';
import { useTrialStatus } from '@/hooks/useTrialStatus';
import { useTranslation } from 'react-i18next';
import { WeightHistoryTab } from '@/components/WeightHistoryTab';

export default function MedicalHome() {
  const { t } = useTranslation();
  const { petId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const { pet, isLoading: isPetLoading, error: petError } = usePet(petId as string);
  const { vaccinations, isLoading: isVaccinationsLoading, deleteVaccinationRecord, refreshVaccinations } = useVaccinations(petId);
  const { appointments, isLoading: isAppointmentsLoading, deleteAppointmentRecord, refreshAppointments } = useAppointments(petId);
  const { records, isLoading: isRecordsLoading, deleteRecord, refreshRecords } = useMedicalRecords(petId);
  const [activeTab, setActiveTab] = useState('overview');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<{id: string, name: string, type: 'vaccination' | 'appointment' | 'record'} | null>(null);
  
  const { subscription } = useSubscription();
  const { isInTrial } = useTrialStatus();
  
  // FREE users can only view, not edit/add (unless in trial)
  const canEdit = subscription?.data?.subscription_tier !== 'free' || isInTrial;

  const isLoading = isPetLoading || isVaccinationsLoading || isAppointmentsLoading || isRecordsLoading;

  if (isLoading) {
    return <div className="p-4 text-center">{t('medical.loading')}</div>;
  }

  if (petError || !pet) {
    return (
      <div className="p-4">
        <Alert variant="destructive">
          <AlertDescription>{t('medical.loadError')}: {petError?.message || t('medical.petNotFound')}</AlertDescription>
        </Alert>
        <Button onClick={() => navigate('/my-pets')} className="mt-4">{t('medical.backToMyPets')}</Button>
      </div>
    );
  }

  const handleTabChange = (value: string) => {
    setActiveTab(value);
  };

  const handleAddNew = () => {
    if (!canEdit) {
      toast({
        title: "🔒 " + t('medical.upgradeRequired'),
        description: t('medical.upgradeToEdit'),
        variant: "destructive"
      });
      navigate('/subscription');
      return;
    }
    
    switch (activeTab) {
      case 'vaccinations':
        navigate(`/medical/${petId}/vaccinations/new`);
        break;
      case 'appointments':
        navigate(`/medical/${petId}/appointments/new`);
        break;
      case 'records':
        navigate(`/medical/${petId}/records/new`);
        break;
      default:
        // On overview tab, let user choose what to add
        navigate(`/medical/${petId}/records/new`);
    }
  };

  const handleDeleteClick = (id: string, name: string, type: 'vaccination' | 'appointment' | 'record') => {
    if (!canEdit) {
      toast({
        title: "🔒 " + t('medical.upgradeRequired'),
        description: t('medical.upgradeToEdit'),
        variant: "destructive"
      });
      navigate('/subscription');
      return;
    }
    setItemToDelete({ id, name, type });
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;
    
    try {
      switch (itemToDelete.type) {
        case 'vaccination':
          await deleteVaccinationRecord(itemToDelete.id);
          toast({ title: t('medical.vaccinationDeleted') });
          break;
        case 'appointment':
          await deleteAppointmentRecord(itemToDelete.id);
          toast({ title: t('medical.appointmentDeleted') });
          break;
        case 'record':
          await deleteRecord(itemToDelete.id);
          toast({ title: t('medical.recordDeleted') });
          break;
      }
    } catch (error) {
      toast({ 
        title: t('medical.deleteError'), 
        description: t('common.tryAgainLater'),
        variant: "destructive" 
      });
    } finally {
      setDeleteDialogOpen(false);
      setItemToDelete(null);
    }
  };

  // Format upcoming vaccinations and appointments for display
  const upcomingVaccinations = vaccinations?.filter(v => new Date(v.date_due) >= new Date())
    .sort((a, b) => new Date(a.date_due).getTime() - new Date(b.date_due).getTime())
    .slice(0, 3);
    
  const upcomingAppointments = appointments?.filter(a => new Date(a.appointment_datetime) >= new Date())
    .sort((a, b) => new Date(a.appointment_datetime).getTime() - new Date(b.appointment_datetime).getTime())
    .slice(0, 3);
    
  const recentRecords = records?.sort((a, b) => 
    new Date(b.record_date).getTime() - new Date(a.record_date).getTime())
    .slice(0, 3);

  return (
    <div className="pb-4">
      <div className="p-4">
        <h2 className="text-lg font-semibold mb-4">{t('medical.petRecordsTitle', { petName: pet.name })}</h2>
        {!canEdit && (
          <Alert className="mb-4 border-amber-200 bg-amber-50">
            <Lock className="h-4 w-4 text-amber-600" />
            <AlertDescription className="text-amber-900">
              <span className="font-medium">{t('medical.viewOnlyMode')}</span> - {t('medical.upgradeToEdit')}
            </AlertDescription>
          </Alert>
        )}
      </div>

      <Tabs defaultValue="overview" className="w-full" onValueChange={handleTabChange}>
        <div className="px-4">
          <TabsList className="w-full grid grid-cols-3 sm:grid-cols-6 gap-1">
            <TabsTrigger value="overview" className="text-xs sm:text-sm px-1 sm:px-3 data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-500 data-[state=active]:to-purple-600 data-[state=active]:text-white">{t('medical.tabs.overview')}</TabsTrigger>
            <TabsTrigger value="vaccinations" className="text-xs sm:text-sm px-1 sm:px-3 data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-500 data-[state=active]:to-purple-600 data-[state=active]:text-white">{t('medical.tabs.vaccinations')}</TabsTrigger>
            <TabsTrigger value="records" className="text-xs sm:text-sm px-1 sm:px-3 data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-500 data-[state=active]:to-purple-600 data-[state=active]:text-white">{t('medical.tabs.records')}</TabsTrigger>
            <TabsTrigger value="appointments" className="text-xs sm:text-sm px-1 sm:px-3 data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-500 data-[state=active]:to-purple-600 data-[state=active]:text-white">{t('medical.tabs.appointments')}</TabsTrigger>
            <TabsTrigger value="weight" className="text-xs sm:text-sm px-1 sm:px-3 data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-500 data-[state=active]:to-purple-600 data-[state=active]:text-white">{t('medical.tabs.weight')}</TabsTrigger>
            <TabsTrigger value="reminders" className="text-xs sm:text-sm px-1 sm:px-3 data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-500 data-[state=active]:to-purple-600 data-[state=active]:text-white">{t('medical.tabs.reminders')}</TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="overview" className="p-4">
          <h2 className="font-semibold text-lg mb-3">{t('medical.overview')}</h2>
          <div className="grid grid-cols-1 gap-4">
            <div className="bg-white p-4 rounded-lg border shadow-sm">
              <h3 className="font-medium text-pet-purple">{t('medical.upcomingVaccinations')}</h3>
              {upcomingVaccinations && upcomingVaccinations.length > 0 ? (
                <ul className="mt-2 space-y-2">
                  {upcomingVaccinations.map(vaccination => (
                    <li key={vaccination.id} className="text-sm border-b pb-2">
                      <div className="font-medium">{vaccination.vaccine_name}</div>
                      <div className="text-gray-500">{t('medical.dueLabel')} {format(new Date(vaccination.date_due), 'MMM d, yyyy')}</div>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-gray-500 text-sm mt-2">{t('medical.noUpcomingVaccinations')}</p>
              )}
              <Button 
                variant="outline" 
                size="sm" 
                className="mt-3 w-full"
                onClick={() => navigate(`/medical/${petId}/vaccinations`)}
              >
                {t('medical.manageVaccinations')}
              </Button>
            </div>
            
            <div className="bg-white p-4 rounded-lg border shadow-sm">
              <h3 className="font-medium text-pet-purple">{t('medical.upcomingAppointments')}</h3>
              {upcomingAppointments && upcomingAppointments.length > 0 ? (
                <ul className="mt-2 space-y-2">
                  {upcomingAppointments.map(appointment => (
                    <li key={appointment.id} className="text-sm border-b pb-2">
                      <div className="font-medium">{appointment.appointment_type}</div>
                      <div className="text-gray-500">
                        {format(new Date(appointment.appointment_datetime), 'MMM d, yyyy h:mm a')}
                      </div>
                      {appointment.vet_name && <div className="text-gray-500">{appointment.vet_name}</div>}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-gray-500 text-sm mt-2">{t('medical.noUpcomingAppointments')}</p>
              )}
              <Button 
                variant="outline" 
                size="sm" 
                className="mt-3 w-full"
                onClick={() => navigate(`/medical/${petId}/appointments`)}
              >
                {t('medical.manageAppointments')}
              </Button>
            </div>
            
            <div className="bg-white p-4 rounded-lg border shadow-sm">
              <h3 className="font-medium text-pet-purple">{t('medical.recentRecords')}</h3>
              {recentRecords && recentRecords.length > 0 ? (
                <ul className="mt-2 space-y-2">
                  {recentRecords.map(record => (
                    <li key={record.id} className="text-sm border-b pb-2">
                      <div className="font-medium">{record.title}</div>
                      <div className="text-gray-500">{record.record_type}</div>
                      <div className="text-gray-500">{format(new Date(record.record_date), 'MMM d, yyyy')}</div>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-gray-500 text-sm mt-2">{t('medical.noRecentRecords')}</p>
              )}
              <Button 
                variant="outline" 
                size="sm" 
                className="mt-3 w-full"
                onClick={() => navigate(`/medical/${petId}/records`)}
              >
                {t('medical.viewAllRecords')}
              </Button>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="vaccinations" className="p-4">
          <h2 className="font-semibold text-lg mb-3">{t('medical.tabs.vaccinations')}</h2>
          {vaccinations && vaccinations.length > 0 ? (
            <ul className="divide-y">
              {vaccinations.map(vaccination => (
                <li key={vaccination.id} className="py-3">
                  <div className="flex justify-between">
                    <div>
                      <h3 className="font-medium">{vaccination.vaccine_name}</h3>
                      <p className="text-sm text-gray-500">{t('medical.dueLabel')} {format(new Date(vaccination.date_due), 'MMM d, yyyy')}</p>
                      {vaccination.date_given && (
                        <p className="text-sm text-gray-500">{t('medical.givenLabel')} {format(new Date(vaccination.date_given), 'MMM d, yyyy')}</p>
                      )}
                      {vaccination.vet_name && <p className="text-xs text-gray-500">{t('medical.vetLabel')} {vaccination.vet_name}</p>}
                    </div>
                     <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        disabled={!canEdit}
                        onClick={() => navigate(`/medical/${petId}/vaccinations/edit/${vaccination.id}`)}
                        className="flex items-center gap-1"
                      >
                        {!canEdit && <Lock className="h-3 w-3" />}
                        {canEdit && <Edit className="h-3 w-3" />}
                        {t('common.edit')}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={!canEdit}
                        onClick={() => handleDeleteClick(vaccination.id, vaccination.vaccine_name, 'vaccination')}
                        className="flex items-center gap-1 text-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-3 w-3" />
                        {t('common.delete')}
                      </Button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <div className="bg-gray-50 p-8 rounded-lg border border-dashed border-gray-300 text-center">
              <BriefcaseMedical className="h-8 w-8 mx-auto text-gray-400 mb-2" />
              <p className="text-gray-500">{t('medical.noVaccinations')}</p>
              <p className="text-gray-500 text-sm mt-1">
                {t('medical.addVaccineHint')}
              </p>
            </div>
          )}
          <Button 
            variant="default" 
            className="mt-4 w-full"
            disabled={!canEdit}
            onClick={() => navigate(`/medical/${petId}/vaccinations/new`)}
          >
            {canEdit ? t('medical.addVaccination') : '🔒 ' + t('medical.upgradeToAdd')}
          </Button>
        </TabsContent>

        <TabsContent value="appointments" className="p-4">
          <h2 className="font-semibold text-lg mb-3">{t('medical.tabs.appointments')}</h2>
          {appointments && appointments.length > 0 ? (
            <ul className="divide-y">
              {appointments.map(appointment => (
                <li key={appointment.id} className="py-3">
                  <div className="flex justify-between">
                    <div>
                      <h3 className="font-medium">{appointment.appointment_type}</h3>
                      <p className="text-sm text-gray-500">
                        {format(new Date(appointment.appointment_datetime), 'MMM d, yyyy h:mm a')}
                      </p>
                      {appointment.vet_name && <p className="text-xs text-gray-500">{t('medical.vetLabel')} {appointment.vet_name}</p>}
                      {appointment.location && <p className="text-xs text-gray-500">{t('medical.locationLabel')} {appointment.location}</p>}
                    </div>
                     <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        disabled={!canEdit}
                        onClick={() => navigate(`/medical/${petId}/appointments/edit/${appointment.id}`)}
                        className="flex items-center gap-1"
                      >
                        {!canEdit && <Lock className="h-3 w-3" />}
                        {canEdit && <Edit className="h-3 w-3" />}
                        {t('common.edit')}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={!canEdit}
                        onClick={() => handleDeleteClick(appointment.id, appointment.appointment_type, 'appointment')}
                        className="flex items-center gap-1 text-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-3 w-3" />
                        {t('common.delete')}
                      </Button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <div className="bg-gray-50 p-8 rounded-lg border border-dashed border-gray-300 text-center">
              <BriefcaseMedical className="h-8 w-8 mx-auto text-gray-400 mb-2" />
              <p className="text-gray-500">{t('medical.noAppointments')}</p>
              <p className="text-gray-500 text-sm mt-1">
                {t('medical.addAppointmentHint')}
              </p>
            </div>
          )}
          <Button 
            variant="default" 
            className="mt-4 w-full"
            disabled={!canEdit}
            onClick={() => navigate(`/medical/${petId}/appointments/new`)}
          >
            {canEdit ? t('medical.scheduleAppointment') : '🔒 ' + t('medical.upgradeToSchedule')}
          </Button>
        </TabsContent>

        <TabsContent value="records" className="p-4">
          <h2 className="font-semibold text-lg mb-3">{t('medical.tabs.records')}</h2>
          {(() => {
            // Combine records, vaccinations, and appointments into a unified list
            const combinedRecords = [
              // Add custom records
              ...(records || []).map(record => ({
                id: record.id,
                title: record.title,
                type: record.record_type,
                date: new Date(record.record_date),
                description: record.description || '',
                recordType: 'medical_record',
                hasAttachments: record.has_attachments || false
              })),
              // Add vaccinations as records
              ...(vaccinations || []).map(vaccination => ({
                id: vaccination.id,
                title: vaccination.vaccine_name,
                type: 'vaccination',
                date: vaccination.date_given ? new Date(vaccination.date_given) : new Date(vaccination.date_due),
                description: vaccination.notes || `${t('medical.dueLabel')} ${format(new Date(vaccination.date_due), 'MMM d, yyyy')}`,
                recordType: 'vaccination',
                hasAttachments: false
              })),
              // Add appointments as records
              ...(appointments || []).map(appointment => ({
                id: appointment.id,
                title: appointment.appointment_type || t('medical.recordTypes.appointment'),
                type: 'appointment',
                date: new Date(appointment.appointment_datetime),
                description: appointment.notes || `${t('medical.withLabel')} ${appointment.vet_name || t('medical.form.veterinarianName')}`,
                recordType: 'appointment',
                hasAttachments: false
              }))
            ];

            // Sort combined records by date (newest first)
            const sortedRecords = combinedRecords.sort((a, b) => b.date.getTime() - a.date.getTime());

            const getRecordTypeDisplay = (type: string) => {
              const typeMap: Record<string, string> = {
                vaccine_record: t('medical.recordTypes.vaccineRecord'),
                diagnosis: t('medical.recordTypes.diagnosis'),
                treatment: t('medical.recordTypes.treatment'),
                surgery: t('medical.recordTypes.surgery'),
                medication_log: t('medical.recordTypes.medicationLog'),
                lab_result: t('medical.recordTypes.labResult'),
                general_note: t('medical.recordTypes.generalNote'),
                vaccination: t('medical.recordTypes.vaccination'),
                appointment: t('medical.recordTypes.appointment')
              };
              return typeMap[type] || t('medical.recordTypes.record');
            };

            const getRecordTypeBadge = (type: string) => {
              const variants: Record<string, string> = {
                vaccine_record: 'bg-blue-100 text-blue-700',
                diagnosis: 'bg-amber-100 text-amber-700',
                treatment: 'bg-green-100 text-green-700',
                surgery: 'bg-purple-100 text-purple-700',
                medication_log: 'bg-pink-100 text-pink-700',
                lab_result: 'bg-cyan-100 text-cyan-700',
                general_note: 'bg-gray-100 text-gray-700',
                vaccination: 'bg-blue-100 text-blue-700',
                appointment: 'bg-purple-100 text-purple-700'
              };
              return variants[type] || 'bg-gray-100 text-gray-700';
            };

            if (sortedRecords.length > 0) {
              return (
                <ul className="divide-y space-y-3">
                  {sortedRecords.map(record => (
                    <li key={`${record.recordType}-${record.id}`} className="py-3 bg-white rounded-lg border p-3">
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex-1">
                          <h3 className="font-medium">{record.title}</h3>
                          <p className="text-xs text-gray-500">{format(record.date, 'MMM d, yyyy')}</p>
                          <p className="text-sm text-gray-600 mt-1">{record.description}</p>
                        </div>
                        <div className="ml-3 flex flex-col gap-2">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRecordTypeBadge(record.type)}`}>
                            {getRecordTypeDisplay(record.type)}
                          </span>
                        </div>
                      </div>
                      <div className="flex gap-2 mt-2">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => {
                            if (record.recordType === 'medical_record') {
                              navigate(`/medical/${petId}/records/${record.id}`);
                            } else if (record.recordType === 'vaccination') {
                              navigate(`/medical/${petId}/vaccinations/edit/${record.id}`);
                            } else if (record.recordType === 'appointment') {
                              navigate(`/medical/${petId}/appointments/edit/${record.id}`);
                            }
                          }}
                          className="flex items-center gap-1"
                        >
                          <Edit className="h-3 w-3" />
                          {record.recordType === 'medical_record' ? t('common.view') : t('common.edit')}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            const deleteType = record.recordType === 'medical_record' ? 'record' : record.recordType as 'vaccination' | 'appointment';
                            handleDeleteClick(record.id, record.title, deleteType);
                          }}
                          className="flex items-center gap-1 text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-3 w-3" />
                          {t('common.delete')}
                        </Button>
                      </div>
                    </li>
                  ))}
                </ul>
              );
            } else {
              return (
                <div className="bg-gray-50 p-8 rounded-lg border border-dashed border-gray-300 text-center">
                  <BriefcaseMedical className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                  <p className="text-gray-500">{t('medical.noRecords')}</p>
                  <p className="text-gray-500 text-sm mt-1">
                    {t('medical.addRecordsHint')}
                  </p>
                </div>
              );
            }
          })()}
          <Button 
            variant="default" 
            className="mt-4 w-full"
            onClick={() => navigate(`/medical/${petId}/records/new`)}
          >
            {t('medical.addRecord')}
          </Button>
        </TabsContent>

        <TabsContent value="weight" className="p-4">
          <h2 className="font-semibold text-lg mb-3">{t('medical.tabs.weight')}</h2>
          <WeightHistoryTab petId={petId as string} />
        </TabsContent>

        <TabsContent value="reminders" className="p-4">
          <MedicalReminderSettings />
        </TabsContent>
      </Tabs>

      <div className="fixed bottom-4 right-6 z-20">
        <Button 
          onClick={handleAddNew}
          className="rounded-full h-12 w-12 bg-pet-purple hover:bg-pet-purple/90 p-0"
        >
          <Plus className="h-6 w-6" />
        </Button>
      </div>

      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={handleDeleteConfirm}
        title={t('medical.deleteItemTitle')}
        description={t('medical.deleteItemDescription', { type: itemToDelete?.type ? t(`medical.recordTypes.${itemToDelete.type}`) : '' })}
        itemName={itemToDelete?.name || ''}
      />
    </div>
  );
}
